import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { CatalogEntityCard, formatCatalogAuditDate } from "@/components/catalog/CatalogEntityCard";
import { GridPaginationFooter } from "@/components/catalog/GridPaginationControls";
import { DatePartsPicker } from "@/components/hr/DatePartsPicker";
import { HrListToolbar, type HrQuickDatePreset } from "@/components/hr/HrListToolbar";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "@/components/ui/sonner";
import { useGridPagination } from "@/hooks/useGridPagination";
import {
  activateLeaveYear,
  createLeaveYear,
  deleteLeaveYear,
  leaveYearErrorMessage,
  listLeaveYears,
  syncLeaveYearsFromApi,
  updateLeaveYear,
  type LeaveYearRecord,
} from "@/lib/hr/leaveYearsStore";
import { fetchTemplateHolidays, type TemplateHoliday } from "@/lib/hr/templateHolidaysStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { endOfDay, format, isValid, parseISO, startOfDay, startOfMonth, subDays } from "date-fns";
import { CalendarIcon, CalendarRange, CheckCircle2, Clock, Edit, Eye, Loader2, Plus, Trash2 } from "lucide-react";
import { motion } from "framer-motion";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

const PAGE_SIZE_OPTIONS = [20, 24, 28, 32, 40, 48, 60] as const;

function formatLeaveYearDetailDate(iso: string): string {
  const d = parseISO(iso);
  return isValid(d) ? format(d, "PP") : "—";
}

type LeaveYearSortBy = "latest" | "oldest" | "name_asc" | "name_desc" | "updated_latest" | "updated_oldest";

export default function LeaveYears() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canView = can(user, "leave_year_can_view");
  const canCreate = can(user, "leave_year_can_create");
  const canUpdate = can(user, "leave_year_can_update");
  const canDelete = can(user, "leave_year_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [version, setVersion] = useState(0);
  const refresh = useCallback(() => setVersion((v) => v + 1), []);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState<LeaveYearSortBy>("latest");
  const [quickDate, setQuickDate] = useState<HrQuickDatePreset>("all");
  const [filterCreatedDate, setFilterCreatedDate] = useState<Date | undefined>(undefined);
  const [filterLabel, setFilterLabel] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<LeaveYearRecord | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LeaveYearRecord | null>(null);
  const [label, setLabel] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [active, setActive] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const [viewYear, setViewYear] = useState<LeaveYearRecord | null>(null);
  const [templateHolidays, setTemplateHolidays] = useState<TemplateHoliday[]>([]);
  const [templateHolidaysLoading, setTemplateHolidaysLoading] = useState(false);
  const [templateImportMode, setTemplateImportMode] = useState<"skip" | "import">("skip");

  const years = useMemo(() => listLeaveYears(), [version]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  useEffect(() => {
    let m = true;
    void (async () => {
      setLoading(true);
      setIsForbidden(false);
      try {
        await syncLeaveYearsFromApi();
        if (m) refresh();
      } catch (e) {
        if (m && isAxiosError(e) && e.response?.status === 403) {
          setIsForbidden(true);
          return;
        }
        console.error(e);
        if (m) toast.error("Could not fetch leave periods. Showing cached data if any.");
      } finally {
        if (m) setLoading(false);
      }
    })();
    return () => {
      m = false;
    };
  }, [refresh]);

  useEffect(() => {
    if (!open) {
      return;
    }
    let m = true;
    void (async () => {
      setTemplateHolidaysLoading(true);
      try {
        const rows = await fetchTemplateHolidays();
        if (m) {
          setTemplateHolidays(rows);
          setTemplateImportMode("skip");
        }
      } catch (e) {
        console.error(e);
        if (m) {
          setTemplateHolidays([]);
        }
        toast.error("Could not load holiday templates.");
      } finally {
        if (m) setTemplateHolidaysLoading(false);
      }
    })();
    return () => {
      m = false;
    };
  }, [open, editing?.id]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return years.filter((y) => {
      if (filterCreatedDate) {
        const want = format(filterCreatedDate, "yyyy-MM-dd");
        const created = parseISO(y.createdAt);
        const got = isValid(created) ? format(created, "yyyy-MM-dd") : null;
        if (got !== want) return false;
      }
      if (quickDate !== "all") {
        const created = parseISO(y.createdAt);
        if (!isValid(created)) return false;
        const now = new Date();
        const end = endOfDay(now);
        const start =
          quickDate === "today" ? startOfDay(now) : quickDate === "week" ? startOfDay(subDays(now, 6)) : startOfMonth(now);
        if (created < start || created > end) return false;
      }
      const labelNeedle = filterLabel.trim().toLowerCase();
      if (labelNeedle && !y.label.toLowerCase().includes(labelNeedle)) return false;
      const matchesQuery =
        !q || y.label.toLowerCase().includes(q) || y.startDate.includes(q) || y.endDate.includes(q);
      return matchesQuery;
    });
  }, [years, query, filterCreatedDate, quickDate, filterLabel]);

  const sorted = useMemo(() => {
    const items = [...filtered];
    items.sort((a, b) => {
      switch (sortBy) {
        case "oldest":
          return a.createdAt.localeCompare(b.createdAt);
        case "name_desc":
          return b.label.localeCompare(a.label);
        case "name_asc":
          return a.label.localeCompare(b.label);
        case "updated_oldest":
          return a.updatedAt.localeCompare(b.updatedAt);
        case "updated_latest":
          return b.updatedAt.localeCompare(a.updatedAt);
        case "latest":
        default:
          return b.createdAt.localeCompare(a.createdAt);
      }
    });
    return items;
  }, [filtered, sortBy]);

  const appliedFiltersCount = (filterCreatedDate ? 1 : 0) + (filterLabel.trim() ? 1 : 0);

  const resetSheetFilters = () => {
    setFilterCreatedDate(undefined);
    setFilterLabel("");
  };

  const paginationKey = `${query}|${sortBy}|${quickDate}|${filterCreatedDate?.getTime() ?? ""}|${filterLabel}`;

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    totalCount,
    totalPages,
    paginatedItems: paginated,
    rangeStart,
    rangeEnd,
  } = useGridPagination(sorted, paginationKey, {
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: PAGE_SIZE_OPTIONS[0],
  });

  const startCreate = () => {
    setEditing(null);
    setLabel("");
    setStartDate("");
    setEndDate("");
    setActive(false);
    setTemplateImportMode("skip");
    setOpen(true);
  };

  const startEdit = (y: LeaveYearRecord) => {
    setEditing(y);
    setLabel(y.label);
    setStartDate(y.startDate);
    setEndDate(y.endDate);
    setActive(y.active);
    setTemplateImportMode("skip");
    setOpen(true);
  };

  const save = async () => {
    if (!label.trim() || !startDate || !endDate) {
      toast.error("Label, start date, and end date are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        const res = await updateLeaveYear(editing.id, {
          label,
          startDate,
          endDate,
          active,
          importTemplateHolidays: templateImportMode === "import",
        });
        toast.success(res?.message ?? "Updated.");
      } else {
        const res = await createLeaveYear({
          label,
          startDate,
          endDate,
          active,
          importTemplateHolidays: templateImportMode === "import",
        });
        toast.success(res.message);
      }
      setOpen(false);
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(leaveYearErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const doActivate = async (id: string) => {
    setActivatingId(id);
    try {
      const res = await activateLeaveYear(id);
      toast.success(res.message);
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(leaveYearErrorMessage(e));
    } finally {
      setActivatingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await deleteLeaveYear(deleteTarget.id);
      toast.success(res.message);
      setDeleteTarget(null);
      refresh();
    } catch (e) {
      console.error(e);
      toast.error(leaveYearErrorMessage(e));
    }
  };

  return isForbidden ? (
    <PermissionDenied
      title="Leave period access required"
      message="You don’t have permission to view leave periods. Ask an administrator to grant leave_year_can_view_any."
    />
  ) : (
    <Layout title="Leave period">
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Leave period</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Set the active leave year used for approvals and balances.{" "}
              <Link to="/dashboard" className="text-primary hover:underline font-medium">
                Home
              </Link>
            </p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2" onClick={startCreate}>
              <Plus className="w-4 h-4" /> Add leave year
            </Button>
          ) : null}
        </div>

        <Dialog open={!!viewYear} onOpenChange={(o) => !o && setViewYear(null)}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Leave period details</DialogTitle>
            </DialogHeader>
            {viewYear ? (
              <dl className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <dt className="text-xs text-muted-foreground">Label</dt>
                  <dd className="font-medium text-foreground">{viewYear.label}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Period</dt>
                  <dd className="text-muted-foreground">
                    {viewYear.startDate} → {viewYear.endDate}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Active</dt>
                  <dd className="text-muted-foreground">{viewYear.active ? "Yes" : "No"}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Created</dt>
                  <dd className="text-muted-foreground">{formatLeaveYearDetailDate(viewYear.createdAt)}</dd>
                </div>
                <div>
                  <dt className="text-xs text-muted-foreground">Last updated</dt>
                  <dd className="text-muted-foreground">{formatLeaveYearDetailDate(viewYear.updatedAt)}</dd>
                </div>
              </dl>
            ) : null}
            <DialogFooter>
              <Button variant="glass" onClick={() => setViewYear(null)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <HrListToolbar
          searchPlaceholder="Search by label or date…"
          query={query}
          onQueryChange={setQuery}
          filtersOpen={filtersOpen}
          onFiltersOpenChange={setFiltersOpen}
          appliedFiltersCount={appliedFiltersCount}
          filterSheetTitle="Filter leave periods"
          filterSheetDescription="Refine by label and created date."
          onResetSheetFilters={resetSheetFilters}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          pageSizeIdPrefix="leave-years"
          sortBy={sortBy}
          onSortByChange={(v) => setSortBy(v as LeaveYearSortBy)}
          sortOptions={[
            { value: "latest", label: "Newest first" },
            { value: "oldest", label: "Oldest first" },
            { value: "name_asc", label: "Name: A to Z" },
            { value: "name_desc", label: "Name: Z to A" },
            { value: "updated_latest", label: "Recently updated" },
            { value: "updated_oldest", label: "Least recently updated" },
          ]}
          quickDate={quickDate}
          onQuickDateChange={setQuickDate}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          filterSheetChildren={
            <div className="rounded-xl border border-border/50 bg-muted/15 p-4 space-y-4">
              <p className="text-sm font-medium text-foreground">Leave year</p>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Label contains</Label>
                <Input
                  className="glass-input h-10"
                  value={filterLabel}
                  onChange={(e) => setFilterLabel(e.target.value)}
                  placeholder="e.g. 2026"
                  autoComplete="off"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Created date</Label>
                <Popover modal={true}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      className={cn("glass-input h-10 w-full justify-start text-left font-normal", !filterCreatedDate && "text-muted-foreground")}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 shrink-0 opacity-70" />
                      {filterCreatedDate ? format(filterCreatedDate, "PPP") : "Any date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="z-[100] w-auto p-0" align="start" sideOffset={6}>
                    <Calendar mode="single" selected={filterCreatedDate} onSelect={setFilterCreatedDate} initialFocus />
                    <div className="flex gap-2 border-t p-2">
                      <Button type="button" variant="ghost" size="sm" className="flex-1" onClick={() => setFilterCreatedDate(undefined)}>
                        Clear
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          }
        />

        {loading ? (
          <div className="glass-card p-8 flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : sorted.length === 0 ? (
          <div className="glass-card p-6 text-sm text-muted-foreground">
            {years.length === 0
              ? canCreate
                ? "No leave periods yet. Click “Add leave year” to create one, then activate it."
                : "No leave periods yet."
              : "No leave periods match your search."}
          </div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {paginated.map((y, i) => (
              <motion.div key={y.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}>
                <CatalogEntityCard
                  title={y.label}
                  statusBadge={
                    y.active
                      ? { label: "Active", className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" }
                      : undefined
                  }
                  primaryMeta={{
                    label: "Period",
                    value: `${y.startDate} → ${y.endDate}`,
                    icon: CalendarRange,
                    iconClassName: "text-sky-600",
                  }}
                  secondaryMeta={{
                    label: "Updated",
                    value: formatCatalogAuditDate(y.updatedAt),
                    icon: Clock,
                    iconClassName: "text-emerald-600",
                  }}
                  footer={`Created: ${formatCatalogAuditDate(y.createdAt)}`}
                  onView={canView ? () => setViewYear(y) : undefined}
                  onEdit={canUpdate ? () => startEdit(y) : undefined}
                  onDelete={canDelete ? () => setDeleteTarget(y) : undefined}
                />
                <div className="mt-2 flex flex-wrap gap-2 px-1">
                  {!y.active && canUpdate ? (
                    <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" disabled={activatingId === y.id} onClick={() => void doActivate(y.id)}>
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      {activatingId === y.id ? "Activating…" : "Activate"}
                    </Button>
                  ) : null}
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {paginated.map((y, i) => (
              <motion.div
                key={y.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.02 }}
                className="glass-card p-3"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="h-16 w-20 rounded-md overflow-hidden bg-muted border border-border/50 shrink-0 flex items-center justify-center">
                    <CalendarRange className="w-8 h-8 text-muted-foreground/40" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-foreground truncate">{y.label}</h3>
                      {y.active ? (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 font-semibold">
                          Active
                        </span>
                      ) : null}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {y.startDate} → {y.endDate}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Updated {formatCatalogAuditDate(y.updatedAt)}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[11px] text-muted-foreground">Created {formatCatalogAuditDate(y.createdAt)}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap ml-auto">
                    {canView ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => setViewYear(y)}>
                        <Eye className="w-3.5 h-3.5" /> View
                      </Button>
                    ) : null}
                    {canUpdate ? (
                      <Button variant="glass" size="sm" className="gap-1.5 h-8 text-xs" onClick={() => startEdit(y)}>
                        <Edit className="w-3.5 h-3.5" /> Edit
                      </Button>
                    ) : null}
                    {!y.active && canUpdate ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5 h-8 text-xs"
                        disabled={activatingId === y.id}
                        onClick={() => void doActivate(y.id)}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        {activatingId === y.id ? "…" : "Activate"}
                      </Button>
                    ) : null}
                    {canDelete ? (
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-destructive" onClick={() => setDeleteTarget(y)} aria-label="Delete">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    ) : null}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {sorted.length > 0 ? (
          <GridPaginationFooter totalCount={totalCount} rangeStart={rangeStart} rangeEnd={rangeEnd} page={page} totalPages={totalPages} onPageChange={setPage} />
        ) : null}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editing ? "Edit leave year" : "New leave year"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Label *</Label>
                <Input className="glass-input h-10" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="2026" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Period Start Date <span className="text-destructive">*</span>
                  </Label>
                  <DatePartsPicker
                    value={startDate}
                    onChange={setStartDate}
                    fromYear={1980}
                    toYear={new Date().getFullYear() + 5}
                    placeholder="Select period start date"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">
                    Period End Date <span className="text-destructive">*</span>
                  </Label>
                  <DatePartsPicker
                    value={endDate}
                    onChange={setEndDate}
                    fromYear={1980}
                    toYear={new Date().getFullYear() + 5}
                    placeholder="Select period end date"
                  />
                </div>
              </div>
              <div className="rounded-lg border border-border/50 bg-muted/10 p-3 space-y-2">
                <p className="text-sm font-medium text-foreground">Template holidays</p>
                <p className="text-xs text-muted-foreground">
                  Reusable month/day holidays can be added to the working calendar for dates that fall inside this leave
                  period (after you save).
                </p>
                {templateHolidaysLoading ? (
                  <p className="text-xs text-muted-foreground">Loading templates…</p>
                ) : templateHolidays.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No template holidays are configured in the system.</p>
                ) : (
                  <>
                    <ul className="text-xs text-muted-foreground max-h-24 overflow-y-auto space-y-0.5 pl-1 border-l-2 border-border/60">
                      {templateHolidays.map((h) => (
                        <li key={h.id}>
                          {h.name}{" "}
                          <span className="text-muted-foreground/80">({h.monthDay})</span>
                        </li>
                      ))}
                    </ul>
                    <RadioGroup
                      className="gap-2 pt-1"
                      value={templateImportMode}
                      onValueChange={(v) => v && setTemplateImportMode(v as "skip" | "import")}
                    >
                      <div className="flex items-start gap-2">
                        <RadioGroupItem value="skip" id="tpl-skip" className="mt-0.5" />
                        <Label htmlFor="tpl-skip" className="text-sm font-normal text-foreground leading-snug cursor-pointer">
                          Skip — do not add calendar entries
                        </Label>
                      </div>
                      <div className="flex items-start gap-2">
                        <RadioGroupItem value="import" id="tpl-add" className="mt-0.5" />
                        <Label htmlFor="tpl-add" className="text-sm font-normal text-foreground leading-snug cursor-pointer">
                          Add — create holidays on the working calendar (year comes from the actual date in this period)
                        </Label>
                      </div>
                    </RadioGroup>
                  </>
                )}
              </div>
              <div className="flex items-start gap-3 rounded-lg border border-border/50 bg-muted/10 p-3">
                <Checkbox
                  id="leave-year-active"
                  checked={active}
                  onCheckedChange={(v) => setActive(v === true)}
                  className="mt-0.5"
                />
                <div className="min-w-0">
                  <Label htmlFor="leave-year-active" className="text-sm font-medium text-foreground cursor-pointer">
                    Active
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    When active, this leave period becomes the default for approvals and balances. Only one leave period can be active at a time.
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={() => setOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void save()} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this leave year?"
          description={deleteTarget?.active ? "Active leave year cannot be deleted. Activate another year first." : "This will remove the leave year."}
          mode="confirm"
          variant="danger"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
}
